/** Parsing and localization for the "Join from your laptop" guide, kept free of JSX so it's
 *  plain-TS testable (vitest.config.ts only picks up *.test.ts, not *.tsx). */

export type GuideNode =
  | { type: "h1" | "h2"; text: string }
  | { type: "p"; text: string }
  | { type: "blockquote"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "code"; lang: string; code: string }
  | { type: "table"; head: string[]; rows: string[][] };

// The guide's placeholder for the hub's address (apps/server/src/app.ts serves on :4317). When
// this very page is loaded from the hub itself — not from a laptop node's own local UI, which
// always sits behind a loopback host — window.location already names where the hub actually is,
// so we show that instead of the doc's example host. Loopback means "don't know": a node proxies
// its own 127.0.0.1 UI to the hub, so its origin says nothing about the hub's real address.
const HUB_PLACEHOLDER = /http:\/\/mac-mini-sl-dolev\.local:4317/g;

export function reachableHubOrigin(location: Pick<Location, "hostname" | "protocol" | "host"> | undefined): string | null {
  if (!location) return null;
  const { hostname, protocol, host } = location;
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") return null;
  return `${protocol}//${host}`;
}

export function localizeGuide(md: string, location: Pick<Location, "hostname" | "protocol" | "host"> | undefined): string {
  const origin = reachableHubOrigin(location);
  return origin ? md.replace(HUB_PLACEHOLDER, origin) : md;
}

export function parseGuide(md: string): GuideNode[] {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const nodes: GuideNode[] = [];
  let i = 0;
  const isBlockStart = (l: string) => l.startsWith("#") || l.startsWith(">") || l.startsWith("```") || /^\s*[-*] /.test(l) || l.trim().startsWith("|");

  const cur = () => lines[i] ?? "";

  while (i < lines.length) {
    const line = cur();
    if (line.trim() === "") {
      i++;
      continue;
    }
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim();
      i++;
      const code: string[] = [];
      while (i < lines.length && !cur().startsWith("```")) {
        code.push(cur());
        i++;
      }
      i++; // closing fence
      nodes.push({ type: "code", lang, code: code.join("\n") });
      continue;
    }
    if (line.startsWith("# ")) {
      nodes.push({ type: "h1", text: line.slice(2) });
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      nodes.push({ type: "h2", text: line.slice(3) });
      i++;
      continue;
    }
    if (line.startsWith(">")) {
      const quote: string[] = [];
      while (i < lines.length && cur().startsWith(">")) {
        quote.push(cur().replace(/^>\s?/, ""));
        i++;
      }
      nodes.push({ type: "blockquote", text: quote.join(" ") });
      continue;
    }
    if (/^\s*[-*] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && cur().trim() !== "" && (/^\s*[-*] /.test(cur()) || /^\s{2,}\S/.test(cur()))) {
        items.push(cur().trim().replace(/^[-*] /, ""));
        i++;
      }
      nodes.push({ type: "ul", items });
      continue;
    }
    if (line.trim().startsWith("|")) {
      const tbl: string[] = [];
      while (i < lines.length && cur().trim().startsWith("|")) {
        tbl.push(cur());
        i++;
      }
      const rows = tbl
        .filter((l) => !/^\s*\|[\s|:-]+\|\s*$/.test(l))
        .map((l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()));
      const [head = [], ...body] = rows;
      nodes.push({ type: "table", head, rows: body });
      continue;
    }
    const p: string[] = [line];
    i++;
    while (i < lines.length && cur().trim() !== "" && !isBlockStart(cur())) {
      p.push(cur());
      i++;
    }
    nodes.push({ type: "p", text: p.join(" ") });
  }
  return nodes;
}
